import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  Input,
  Select,
  InputNumber,
  Checkbox,
  DatePicker,
} from "antd";
import {
  bloodGroupOptions,
  genderKeys,
  govtIdTypeOptions,
  maritalStatusOptions,
  occupationOptions,
} from "../receptionKeys";
import inputStyles from "../../../styles/Custom/Input.module.css";
import PatientMobileSearch from "../PatientMobileSearch";
import { validateConfig } from "../../../config/validate.config";
import dayjs from "dayjs";
import useReceptionistMobilePickerProps from "../receptionistMobilePickerProps";

const BasicDetails = ({
  searchedText,
  setSearchedText,
  selectedId,
  setSelectedId,
  editData,
  form,
  ipData,
}) => {
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const [sameAsAddress, setSameAsAddress] = useState(false);

  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }

  useEffect(() => {
    if (sameAsAddress) {
      const address = form.getFieldValue("address");
      form.setFieldsValue({ permanent_address: address });
    } else {
      form.setFieldsValue({
        permanent_address: editData?.permanent_address || null,
      });
    }
  }, [sameAsAddress, form, editData]);

  useEffect(() => {
    form.setFieldValue("age_by_dob", null);
  }, [selectedId, editData?.id, form]);

  return (
    <>
      <Col xs={24} md={8}>
        <PatientMobileSearch
          searchedText={searchedText}
          setSearchedText={setSearchedText}
          setSelectedId={setSelectedId}
          selectedId={selectedId}
          id={editData?.id || ipData?.patient_id}
          form={form}
        />
      </Col>

      <Col xs={24} md={8}>
        <Form.Item
          label="First Name"
          rules={validateConfig.isRequired("Patient Name")}
          name="first_name"
        >
          <Input
            className={inputStyles.smInput}
            placeholder="Enter first name"
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item
          label="Last Name"
          name="last_name"
          rules={validateConfig.isRequired("Last Name")}
        >
          <Input
            className={inputStyles.smInput}
            placeholder="Enter last name"
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item
          label="Date Of Birth"
          name="dob"
          rules={[{ required: true, message: "Enter DOB or Age" }]}
        >
          <DatePicker
            {...mobilePickerProps}
            disabledDate={disabledDate}
            className={inputStyles.smDateInput}
            format="YYYY-MM-DD"
            placeholder="Select DOB"
            onChange={(date) => {
              if (date) {
                const age = dayjs().diff(date, "year");
                form.setFieldValue("age_by_dob", age);
              } else {
                form.setFieldValue("age_by_dob", null);
              }
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item
          label="Age"
          name="age_by_dob"
          rules={[
            {
              validator: (_, value) => {
                if (value === undefined || value === null || value === "") {
                  return Promise.resolve();
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            min={0}
            max={120}
            precision={0}
            step={1}
            className={inputStyles.mdNumberInput}
            placeholder="Enter Age"
            onChange={(age) => {
              if (age !== null && age !== undefined) {
                const year = dayjs().year() - age;
                form.setFieldValue("dob", dayjs(`${year}-01-01`));
              }
            }}
            onKeyDown={(e) => {
              if (["-", "+", "e", ".", ","].includes(e.key)) {
                e.preventDefault();
              }
            }}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item
          label="Gender"
          name="gender"
          rules={validateConfig.isRequired("Gender")}
        >
          <Select
            allowClear
            placeholder="Select Gender"
            className={inputStyles.smSelect}
            options={genderKeys}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={16}>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            {
              validator: (_, value) => {
                if (!value || !value.trim()) return Promise.resolve();
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value.trim())) {
                  return Promise.reject(
                    new Error("Please enter a valid email")
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder="Enter email"
            className={inputStyles.smInput}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item label="Blood Group" name="blood_group">
          <Select
            allowClear
            placeholder="Blood Group"
            className={inputStyles.smSelect}
            options={bloodGroupOptions}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={12}>
        <Form.Item label="Address" name="address">
          <Input.TextArea
            placeholder="Address"
            className={inputStyles.smInput}
            rows={2}
          />
        </Form.Item>
        <Checkbox
          checked={sameAsAddress}
          onChange={(e) => setSameAsAddress(e.target.checked)}
          className="inpatient-same-address-checkbox"
        >
          Same as Address
        </Checkbox>
      </Col>

      <Col xs={24} md={12}>
        <Form.Item label="Permanent Address" name="permanent_address">
          <Input.TextArea
            placeholder="Permanent Address"
            className={inputStyles.smInput}
            rows={2}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item label="Marital Status" name="marital_status">
          <Select
            allowClear
            placeholder="Marital Status"
            className={inputStyles.smSelect}
            options={maritalStatusOptions}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item label="Occupation" name="occupation">
          <Select
            allowClear
            placeholder="Occupation"
            className={inputStyles.smSelect}
            options={occupationOptions}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item label="Govt ID Type" name="govt_id_type">
          <Select
            allowClear
            placeholder="Govt ID Type"
            className={inputStyles.smSelect}
            options={govtIdTypeOptions}
          />
        </Form.Item>
      </Col>

      <Col xs={24} md={8}>
        <Form.Item label="Govt ID No" name="govt_id_no">
          <Input
            className={inputStyles.smInput}
            placeholder="Government ID Number"
          />
        </Form.Item>
      </Col>
    </>
  );
};

export default BasicDetails;
